import { Icon } from '@iconify/react';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import { ConfirmationModal, SectionTitle, Stepper } from '../../../../shared/components';
import SuccessModal from '../../../../shared/components/feedback/SuccessModal';
import { mapProjectFormToApiPayload } from '../../../domain/mappers/projectPayload.mapper';
import { ensureProjectPocIds } from '../../../services/poc.service';
import { createProject } from '../../../services/work.service';
import { PROJECT_FORM_STEPS } from '../constants';
import { ALL_FIELDS, STEP_FIELD_MAP } from '../constants/stepFieldMap';
import { defaultValues } from '../schema/defaultValues';
import StepAccounting from '../steps/StepAccounting';
import StepBasicDetails from '../steps/StepBasicDetails';
import StepFiles from '../steps/StepFiles';
import StepPOC from '../steps/StepPOC';
import StepQuotation from '../steps/StepQuotation';
import StepSanctionDetails from '../steps/StepSanctionDetails';
import StepTypeOfWork from '../steps/StepTypeOfWork';

// v2: the work_jobs2 fields moved under a `details` branch, so v1 drafts no longer load.
const PROJECT_DRAFT_KEY = 'project_onboarding_draft_v2';

/**
 * Keyed by step so inserting or reordering a step in PROJECT_FORM_STEPS cannot
 * desync the rendered panel from the stepper (which positional indices did).
 */
const STEP_COMPONENTS = {
  basic: StepBasicDetails,
  sanction: StepSanctionDetails,
  type: StepTypeOfWork,
  poc: StepPOC,
  accounting: StepAccounting,
  quotation: StepQuotation,
  files: StepFiles,
};

/** A new job is dated today unless the user picks another day; a resumed draft keeps its own. */
const freshDefaults = () => ({ ...defaultValues, projectDate: dayjs() });

const FINALIZE_FROM_STEP_INDEX = PROJECT_FORM_STEPS.findIndex((s) => s.key === 'accounting');

function serializeDraft(values) {
  return {
    ...values,
    projectDate: values.projectDate ? values.projectDate : null,
  };
}

function deserializeDraft(values) {
  return {
    ...values,
    projectDate: values.projectDate ? dayjs(values.projectDate) : null,
  };
}

export default function ProjectFormLayout({ mode }: { mode?: any } = {}) {
  const navigate = useNavigate();

  const [draftResolved, setDraftResolved] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [activeStep, setActiveStep] = useState(0);
  const steps = PROJECT_FORM_STEPS.map((step, index) => ({
    ...step,
    completed: index < activeStep,
  }));
  const ActiveStepComponent = STEP_COMPONENTS[PROJECT_FORM_STEPS[activeStep]?.key];

  const methods = useForm({
    defaultValues: freshDefaults(),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    shouldFocusError: true,
  });
  const { handleSubmit, watch, reset, trigger } = methods;

  useEffect(() => {
    const draft = localStorage.getItem(PROJECT_DRAFT_KEY);
    if (!draft) {
      setDraftResolved(true);
      return;
    }
    setShowDraftModal(true);
  }, []);

  useEffect(() => {
    if (!draftResolved) return;

    const subscription = watch((values) => {
      localStorage.setItem(PROJECT_DRAFT_KEY, JSON.stringify(serializeDraft(values)));
    });

    return () => subscription.unsubscribe();
  }, [watch, draftResolved]);

  const handleResumeDraft = () => {
    const draft = localStorage.getItem(PROJECT_DRAFT_KEY);
    if (!draft) return;

    reset(deserializeDraft(JSON.parse(draft)));
    setDraftResolved(true);
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem(PROJECT_DRAFT_KEY);
    reset(freshDefaults());
    setDraftResolved(true);
  };

  const handleStepClick = async (stepIndex) => {
    if (stepIndex <= activeStep) {
      setActiveStep(stepIndex);
      return;
    }

    const currentStepKey = PROJECT_FORM_STEPS[activeStep].key;
    const fields = STEP_FIELD_MAP[currentStepKey] ?? [];

    if (!fields.length) {
      setActiveStep(stepIndex);
      return;
    }

    const isValid = await trigger(fields, { shouldFocus: true });
    if (!isValid) return;

    setActiveStep(stepIndex);
  };

  const handlePrevious = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = async () => {
    const currentStepKey = PROJECT_FORM_STEPS[activeStep].key;
    const fields = STEP_FIELD_MAP[currentStepKey] ?? [];

    const isValid = await trigger(fields, { shouldFocus: true });

    if (!isValid) return;
    setActiveStep((prev) => prev + 1);
  };

  const validateUpToFinalize = async () => {
    return trigger(ALL_FIELDS, { shouldFocus: true });
  };

  const handleFinalizeClick = async () => {
    const isValid = await validateUpToFinalize();
    if (!isValid) return;

    setShowFinalizeModal(true);
  };

  const handleFinalSubmit = handleSubmit(async (values) => {
    try {
      setSubmitting(true);

      // Contacts are linked by id, so any that were typed rather than picked from the
      // directory become `/api/poc/*` rows first.
      const pocIds = await ensureProjectPocIds(values);
      const payload = mapProjectFormToApiPayload(values, pocIds);

      await createProject(payload);

      localStorage.removeItem(PROJECT_DRAFT_KEY);
      setShowFinalizeModal(false);
      setShowSuccessModal(true);
    } catch (error) {
      toast.error(
        logError({
          message: 'Failed to create project',
          error,
          code: 'PROJECT_CREATE_FAILED',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  });

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate('/', { replace: true });
  };

  useEffect(() => {
    if (import.meta.env.MODE !== 'development') return;

    PROJECT_FORM_STEPS.forEach((step) => {
      if (!STEP_FIELD_MAP[step.key]?.length) {
        console.warn(`STEP_FIELD_MAP has no fields for step: ${step.key}`);
      }
    });
  }, []);

  return (
    <>
      <div className='column gap-3'>
        <SectionTitle title='New Job Creation' />
        <FormProvider {...methods}>
          <form className='column gap-3'>
            <Stepper steps={steps} activeStep={activeStep} onStepClick={handleStepClick} />
            {ActiveStepComponent ? <ActiveStepComponent /> : null}

            <div className='row row--between'>
              <button
                type='button'
                disabled={activeStep === 0}
                onClick={handlePrevious}
                className='btn text-btn'
              >
                <Icon icon='icon-park-outline:left' width={18} />
                Previous
              </button>

              {activeStep < FINALIZE_FROM_STEP_INDEX ? (
                <button type='button' onClick={handleNext} className='btn cta-btn'>
                  Next
                  <Icon icon='icon-park-outline:right' width={18} />
                </button>
              ) : (
                <button type='button' onClick={handleFinalizeClick} className='btn cta-btn'>
                  Finalize work
                </button>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
      <ConfirmationModal
        open={showDraftModal}
        onClose={() => {
          handleDiscardDraft();
          setShowDraftModal(false);
        }}
        title='Resume draft?'
        message={
          <Typography sx={{ fontSize: '1rem', color: 'grey.500', textAlign: 'center' }}>
            A saved project draft was found.
            <br />
            Do you want to continue where you left off or start fresh?
          </Typography>
        }
        onConfirm={() => {
          handleResumeDraft();
          setShowDraftModal(false);
        }}
        confirmText='Resume'
        cancelText='Discard'
      />
      <ConfirmationModal
        open={showFinalizeModal}
        onClose={() => setShowFinalizeModal(false)}
        title='Finalize work?'
        message={
          <Typography sx={{ fontSize: '1rem', color: 'grey.500', textAlign: 'center' }}>
            Once finalized, this job will be created with the current details.
            <br />
            You can still edit it later if needed.
          </Typography>
        }
        confirmText='Finalize'
        cancelText='Review'
        onConfirm={handleFinalSubmit}
        loading={submitting}
      />
      <SuccessModal
        open={showSuccessModal}
        onClose={handleSuccessClose}
        message='New Project created successfully.' maxWidth='sm'
      />
    </>
  );
}
